import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const OnboardingPopup = ({ onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const res = await fetch(`${API}/onboarding/status`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (data.needs_onboarding && data.questions?.length > 0) {
            setQuestions(data.questions);
          } else {
            onComplete();
          }
        } else {
          onComplete();
        }
      } catch {
        onComplete();
      }
    };
    checkOnboarding();
  }, [onComplete]);

  const handleAnswer = (questionId, value) => {
    const updated = { ...answers, [questionId]: value };
    setAnswers(updated);

    // Auto-advance or submit
    if (currentIdx < questions.length - 1) {
      setTimeout(() => setCurrentIdx(currentIdx + 1), 300);
    } else {
      submitAnswers(updated);
    }
  };

  const submitAnswers = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/onboarding/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ answers: finalAnswers }),
      });
      const data = await res.json();
      if (res.ok) {
        setResultMessage(data.message || "You're all set!");
        setDone(true);
        setTimeout(() => onComplete(), 2000);
      } else {
        toast.error(data.detail || "Something went wrong");
        setSubmitting(false);
      }
    } catch {
      toast.error("Failed to save. Please try again.");
      setSubmitting(false);
    }
  };

  if (questions.length === 0) return null;

  const q = questions[currentIdx];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#070B14]/95 backdrop-blur-md" data-testid="onboarding-popup">
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="done"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-lg mx-auto px-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
            >
              <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
            </motion.div>
            <h2 className="text-3xl font-bold text-white mb-3">All Set!</h2>
            <p className="text-slate-400 text-lg">{resultMessage}</p>
          </motion.div>
        ) : (
          <motion.div
            key={`q-${currentIdx}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-2xl mx-auto px-6"
          >
            {/* Progress */}
            {questions.length > 1 && (
              <div className="flex gap-2 justify-center mb-8">
                {questions.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      i <= currentIdx ? "bg-blue-500 w-8" : "bg-slate-700 w-4"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Category badge */}
            <div className="text-center mb-4">
              <span className="text-xs uppercase tracking-widest text-blue-400 font-semibold">
                {q.category}
              </span>
            </div>

            {/* Question */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10 leading-snug" data-testid="onboarding-question">
              {q.question}
            </h2>

            {/* Answer buttons */}
            <div className="flex flex-col gap-4 max-w-md mx-auto">
              {q.options.map((opt) => {
                const isSelected = answers[q.id] === opt.value;
                return (
                  <motion.button
                    key={opt.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswer(q.id, opt.value)}
                    disabled={submitting}
                    className={`w-full py-5 px-6 rounded-2xl text-lg font-semibold transition-all duration-200 border-2 ${
                      isSelected
                        ? "bg-blue-600 border-blue-500 text-white"
                        : "bg-[#0f172a] border-[#1E293B] text-white hover:border-blue-500/50 hover:bg-[#131d35]"
                    } ${submitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    data-testid={`onboarding-option-${opt.value}`}
                  >
                    {opt.label}
                  </motion.button>
                );
              })}
            </div>

            {submitting && (
              <div className="text-center mt-8">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-slate-500 text-sm mt-2">Setting up your workouts...</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingPopup;
